import { connect } from 'react-redux';
import { createStructuredSelector, createSelector } from 'reselect';
import subscriptionsSelector from 'selectors/page/autoReplenishment/subscriptions/subscriptionsSelector';
import BeautyInsiderSelector from 'selectors/beautyInsider/beautyInsiderSelector';
import AutoReplenishmentActions from 'actions/AutoReplenishmentActions';
import beautyInsiderActions from 'actions/BeautyInsiderActions';
import AddressActions from 'actions/AddressActions';
import { userSelector } from 'selectors/user/userSelector';
import cmsDataSelector from 'selectors/page/autoReplenishment/cmsData/cmsDataSelector';
import userUtils from 'utils/User';
import FrameworkUtils from 'utils/framework';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import AutoReplenishmentBindings from 'analytics/bindingMethods/pages/myAccount/AutoReplenishmentBindings';
import EN from 'components/RichProfile/MyAccount/AutoReplenishment/locales/AutoReplenishment_en_US.js';
import FR from 'components/RichProfile/MyAccount/AutoReplenishment/locales/AutoReplenishment_fr_CA.js';
import EN_MANAGE_SUBS from 'components/GlobalModals/ManageSubscriptionModal/locales/ManageSubscriptionModal_en_US.js';
import FR_MANAGE_SUBS from 'components/GlobalModals/ManageSubscriptionModal/locales/ManageSubscriptionModal_fr_CA.js';
import Actions from 'actions/Actions';
import Empty from 'constants/empty';
import ShippingAndPaymentInfoSelector from 'selectors/page/autoReplenishment/shippingAndPaymentInfo/shippingAndPaymentInfoSelector';

const { wrapHOC } = FrameworkUtils;
const { beautyInsiderSelector } = BeautyInsiderSelector;
const { getTextFromResource, getLocalisedDataFromResource, getCurrentCountry } = LanguageLocaleUtils;

const getText = getLocalisedDataFromResource(EN, FR);
const getTextFreq = getLocalisedDataFromResource(EN_MANAGE_SUBS, FR_MANAGE_SUBS);

const { shippingAndPaymentInfoSelector } = ShippingAndPaymentInfoSelector;

const fields = createSelector(
    subscriptionsSelector,
    shippingAndPaymentInfoSelector,
    userSelector,
    beautyInsiderSelector,
    cmsDataSelector,
    createStructuredSelector({
        viewMore: getTextFromResource(getText, 'viewMore'),
        viewMoreSubscriptions: getTextFromResource(getText, 'viewMoreSubscriptions'),
        needHelp: getTextFromResource(getText, 'needHelp'),
        viewFAQ: getTextFromResource(getText, 'viewFAQ'),
        deliveryFrequency: getTextFromResource(getTextFreq, 'deliveryFrequency'),
        autoReplenishSummary: getTextFromResource(getTextFreq, 'autoReplenishSummary'),
        activeText: getTextFromResource(getTextFreq, 'active'),
        pausedText: getTextFromResource(getTextFreq, 'paused'),
        canceledText: getTextFromResource(getText, 'canceled'),
        viewShipments: getTextFromResource(getTextFreq, 'viewShipments'),
        biPointsEarned: getTextFromResource(getTextFreq, 'biPointsEarned'),
        totalSaving: getTextFromResource(getTextFreq, 'totalSaving'),
        points: getTextFromResource(getTextFreq, 'points'),
        cvvSubmitButtonText: getTextFromResource(getTextFreq, 'cvvSubmitButtonText'),
        rateOf: getTextFromResource(getText, 'rateOf', ['{0}']),
        canceledSubscriptionsHeading: getTextFromResource(getText, 'canceledSubscriptionsHeading'),
        canceledSubscriptionsSubheading: getTextFromResource(getText, 'canceledSubscriptionsSubheading'),
        headerItem: getTextFromResource(getText, 'headerItem'),
        headerPrice: getTextFromResource(getText, 'headerPrice'),
        activeSubscriptionsCount: getTextFromResource(getText, 'activeSubscriptionsCount', ['{0}', '{1}']),
        pausedSubscriptionsCount: getTextFromResource(getText, 'pausedSubscriptionsCount', ['{0}', '{1}']),
        canceledSubscriptionsCount: getTextFromResource(getText, 'canceledSubscriptionsCount', ['{0}', '{1}'])
    }),
    (allSubscriptions, subscriptionShippingPayment, user, beautyInsider, cmsData, textResources) => {
        const {
            hasNext, active, paused, cancelled, subscriptions, subscriptionList, totalAggregateDiscount
        } = allSubscriptions;
        const { firstName, profileId } = user;
        const biPoints = beautyInsider.summary?.clientSummary?.currentYear?.subscriptionPtsEarned?.value || 0;
        const isLoggedIn = userUtils.isSignedIn();
        const displaySignIn = user.isInitialized && !isLoggedIn;

        const config = (typeof Sephora !== 'undefined' && Sephora.configurationSettings) || Empty.Object;
        const { autoReplenishmentConfigurations } = config;

        const resumeDesignConfig = autoReplenishmentConfigurations?.resume?.enhancedDesign ?? Empty.Object;

        const hasAddressCVVCheck = !!autoReplenishmentConfigurations?.addressUpdateCVVCheck?.isEnabled;
        const hasNewManageDesign = !!autoReplenishmentConfigurations?.changeDate?.newDesign?.isEnabled;
        const hasNewResumeDesign = !!(resumeDesignConfig.isEnabled && resumeDesignConfig[`isEnabled${getCurrentCountry()}`]);

        return {
            ...textResources,
            hasNext,
            active,
            paused,
            cancelled,
            subscriptions,
            subscriptionList,
            cmsData,
            totalAggregateDiscount,
            firstName,
            profileId,
            isLoggedIn,
            displaySignIn,
            biPoints,
            hasAddressCVVCheck,
            hasNewManageDesign,
            hasNewResumeDesign,
            currentSubscriptionPaymentData: subscriptionShippingPayment?.payment
        };
    }
);

const functions = {
    showInterstice: Actions.showInterstice,
    showConfirmCVVModal: Actions.showConfirmCVVModal,
    loadSubscriptions: AutoReplenishmentActions.loadSubscriptions,
    updateActiveSubscriptions: AutoReplenishmentActions.updateActiveSubscriptions,
    updatePausedSubscriptions: AutoReplenishmentActions.updatePausedSubscriptions,
    updateCancelledSubscriptions: AutoReplenishmentActions.updateCancelledSubscriptions,
    openFAQModal: AutoReplenishmentActions.openFAQModal,
    unsubscribeAutoReplenishment: AutoReplenishmentActions.unsubscribeAutoReplenishment,
    pauseAutoReplenishment: AutoReplenishmentActions.pauseAutoReplenishment,
    skipAutoReplenishment: AutoReplenishmentActions.skipAutoReplenishment,
    openConfirmUnsubscribeModal: AutoReplenishmentActions.openConfirmUnsubscribeModal,
    openConfirmPausedSubscriptionModal: AutoReplenishmentActions.openConfirmPausedSubscriptionModal,
    updateAutoReplenishmentSubscription: AutoReplenishmentActions.updateAutoReplenishmentSubscription,
    fetchClientSummary: beautyInsiderActions.fetchClientSummary,
    removeCreditCard: AutoReplenishmentActions.removeCreditCard,
    setDefaultCCOnProfileAndDelete: AutoReplenishmentActions.setDefaultCCOnProfileAndDelete,
    resumeAutoReplenishment: AutoReplenishmentActions.resumeAutoReplenishment,
    manageUnsubscribe: AutoReplenishmentBindings.manageUnsubscribe,
    pauseConfirmation: AutoReplenishmentBindings.pauseConfirmation,
    unsubscribeModalClose: AutoReplenishmentBindings.unsubscribeModalClose,
    skipModalClose: AutoReplenishmentBindings.skipModalClose,
    resumeModalConfirm: AutoReplenishmentBindings.resumeModalConfirm,
    addCardModalClose: AutoReplenishmentBindings.addCardModalClose,
    skipConfirmation: AutoReplenishmentBindings.skipConfirmation,
    loadMoreTracking: AutoReplenishmentBindings.loadMore,
    loadContentfulContent: AutoReplenishmentActions.loadContentfulContent,
    openRemoveAddressModal: AutoReplenishmentActions.openRemoveAddressModal,
    openLimitReachedModal: AutoReplenishmentActions.openLimitReachedModal,
    getSavedAddresses: AddressActions.getSavedAddresses,
    setDefaultAddress: AddressActions.setDefaultAddress,
    deleteSavedAddress: AddressActions.deleteAddress,
    updateDefaultShippingAddressData: AddressActions.updateDefaultShippingAddressData,
    updateSubscriptionAddress: AutoReplenishmentActions.updateSubscriptionAddress,
    loadSubscription: AutoReplenishmentActions.loadSubscription,
    refreshSubscriptions: AutoReplenishmentActions.refreshSubscriptions,
    orderNow: AutoReplenishmentActions.orderNow,
    openOrderConfirmationModal: AutoReplenishmentActions.openOrderConfirmationModal,
    openOrderConfirmationFailure: AutoReplenishmentActions.openOrderConfirmationFailure,
    openOrderNowTooSoonModal: AutoReplenishmentActions.openOrderNowTooSoonModal
};

const withAutoReplenishmentProps = wrapHOC(connect(fields, functions));

export {
    withAutoReplenishmentProps, fields, functions
};
